import { useFetchListData } from '@/composables'
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CountryItem, CountryResponse } from './types'

const useCountryStore = defineStore('country', () => {
  const { locale } = useI18n()
  const { loading, data, fetchData } = useFetchListData<CountryResponse[]>({
    url: '/api/pub/get_country',
    autoFetch: false,
    initialData: [],
  })

  // 排除国家列表
  const EXCLUDE_COUNTRIES = ['United Kingdom', 'United States of America', 'Australia']

  const i18nCountryList = computed(() => {
    if (!Array.isArray(data.value)) return []

    // 排除国家列表
    const filteredData = data.value.filter((item) => !EXCLUDE_COUNTRIES.includes(item.name))

    // 判断是否中文语言环境
    const isChineseLocale = locale.value === 'zh-CN' || locale.value === 'zh-TW'

    return filteredData.map((item) => {
      const countryCode = item.code?.toUpperCase() || item.name?.slice(0, 2).toUpperCase()
      const countryDisplayName = isChineseLocale ? item.mark || item.name : item.name
      return {
        key: `${countryCode}-${item.phone_code}`,
        countryEnName: item.name,
        countryDisplayName,
        countryCode,
        phoneCode: item.phone_code,
        flag: countryCode,
        checked: item.checked,
      }
    })
  })

  const countrySelectorOptions = computed(() => {
    return (i18nCountryList.value || []).map((countryItem) => {
      const value = countryItem.countryCode
      const labelText = `${countryItem.countryCode ?? ''} ${countryItem.countryDisplayName ?? ''}`
      return {
        value,
        label: labelText,
        flag: countryItem.flag,
        countryEnName: countryItem.countryEnName,
      }
    })
  })

  // 默认选中项（取第一个 checked===1；没有则返回 null）
  const defaultCountry = computed<CountryItem | null>(() => {
    const list = i18nCountryList.value || []
    const idx = list.findIndex((c) => Number(c.checked) === 1)
    return idx >= 0 ? list[idx] : null
  })

  const reset = () => {
    loading.value = false
    data.value = []
    _initPromise = null
  }

  // Idempotent initializer
  let _initPromise: Promise<void> | null = null
  const init = async () => {
    if (_initPromise) return _initPromise
    _initPromise = (async () => {
      if (Array.isArray(data.value) && data.value.length > 0) {
        return
      }
      await fetchData()
    })()
    return _initPromise
  }

  return {
    data,
    defaultCountry,
    i18nCountryList,
    loading,
    countrySelectorOptions,
    fetchData,
    init,
    reset,
  }
})

export default useCountryStore
export { useCountryStore }
